/**
 * Модульное (не-React) состояние сессии.
 *
 * Почему не React-state: apiFetch читает access-токен СИНХРОННО и вне дерева React —
 * из хука это невозможно. React-часть (AuthProvider) подписывается на этот же стор
 * через useSyncExternalStore, поэтому источник истины остаётся один.
 *
 * Фабрика общая для пользовательского и админского пространств токенов, но КАЖДЫЙ вызов
 * createSessionStore() замыкает СВОЙ набор переменных. Протечка между пространствами
 * невозможна на уровне области видимости, а не запрещена соглашением «не перепутай».
 */
export type AuthStatus = 'loading' | 'guest' | 'authenticated';

export interface SessionSnapshot<User> {
  readonly status: AuthStatus;
  readonly user: User | null;
}

export interface SessionStore<User> {
  getAccessToken(): string | null;
  getCsrfToken(): string | null;
  getSnapshot(): SessionSnapshot<User>;
  subscribe(listener: () => void): () => void;
  setSession(user: User, accessToken: string, csrfToken: string | null): void;
  updateUser(user: User): void;
  clear(): void;
}

export function createSessionStore<User>(initialCsrfToken: string | null): SessionStore<User> {
  let accessToken: string | null = null;
  let csrfToken: string | null = initialCsrfToken;
  let snapshot: SessionSnapshot<User> = { status: 'loading', user: null };
  const listeners = new Set<() => void>();

  function emit(): void {
    for (const listener of listeners) listener();
  }

  return {
    getAccessToken: () => accessToken,
    getCsrfToken: () => csrfToken,
    // Ссылка на snapshot меняется ТОЛЬКО вместе с содержимым: useSyncExternalStore
    // сравнивает по ссылке и на новом объекте при каждом вызове ушёл бы в бесконечный рендер.
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    setSession(user, nextAccessToken, nextCsrfToken) {
      accessToken = nextAccessToken;
      // CSRF-токен перевыпускается на каждой ротации сессии; null означает «ответ его
      // не принёс» (мобильный режим), и тогда прежнее значение сохраняется.
      if (nextCsrfToken !== null) csrfToken = nextCsrfToken;
      snapshot = { status: 'authenticated', user };
      emit();
    },
    updateUser(user) {
      snapshot = { status: 'authenticated', user };
      emit();
    },
    clear() {
      accessToken = null;
      snapshot = { status: 'guest', user: null };
      emit();
    },
  };
}
